import React, { useEffect, useState, useMemo } from "react";
import { CheckCircle, ThumbsDown, Star, MessageCircle, X, ChevronLeft, ChevronRight } from "lucide-react";
import LoadingScreen from "../../components/LoadingScreen";
import apiClient from "../../services/apiClient";

// ====== Tabs ======
const TABS = [
  { label: "Pending", status: "pending" },
  { label: "Approved", status: "approved" },
  { label: "Rejected", status: "rejected" },
  { label: "On Hold", status: "on hold" },
];

// ====== Review Type ======
interface Review {
  id: number;
  reviewer: string;
  reviewer_email?: string;
  rating: number;
  comment: string;
  service_name: string;
  status: string;
  created_at: string;
  type: "vet" | "service"; // type to distinguish reviews
}

// ====== Fetch Reviews Hook ======
const useReviews = (status: string, providerId?: number, serviceProviderId?: number) => {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchReviews = async () => {
    setLoading(true);
    try {
      const params: any = {};
      if (providerId) params.provider_id = providerId;
      if (serviceProviderId) params.service_provider_id = serviceProviderId;

      const queryString = new URLSearchParams(params).toString();
      const { data } = await apiClient(`/v1/resource/ConsultReview?${queryString}`, { method: "GET" }) as any;

      const mappedReviews: Review[] = data?.consult_reviews?.map((r: any) => {
        const isVet = r.consult_id && r.provider_id;
        const isService = r.service_id && r.service_provider_id;
        const reviewerFirstName = r.patient?.first_name || "";
        const reviewerLastName = r.patient?.last_name || "";
        const providerFullName = isVet
          ? `${r.provider?.first_name || ""} ${r.provider?.last_name || ""}`.trim() || "Unknown Vet"
          : `${r.serviceProvider?.first_name || ""} ${r.serviceProvider?.last_name || ""}`.trim() || "Unknown Service";
        return {
          id: r.id,
          reviewer: reviewerFirstName || reviewerLastName
            ? `${reviewerFirstName} ${reviewerLastName}`.trim()
            : "Unknown",
          reviewer_email: r.patient?.email,
          rating: r.rating,
          comment: r.comment,
          service_name: providerFullName,
          status: r.status.toLowerCase(),
          created_at: r.created_at,
          type: isVet ? "vet" : isService ? "service" : "service",
        };
      }) || [];

      setReviews(mappedReviews.filter(r => r.status === status));
    } catch (err) {
      console.error("Error fetching reviews", err);
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (
    id: number,
    _type: "vet" | "service",
    action: "approve" | "reject" | "hold"
  ) => {
    try {
      let is_approved = 0;

      if (action === "approve") is_approved = 1;  // pass 1
      else if (action === "reject") is_approved = 2; // pass 2
      else if (action === "hold") is_approved = 3;   // pass 3

      await apiClient(`/v1/resource/consult_reviews/${id}`, {
        method: "PATCH",
        body: { is_approved },
      });

      fetchReviews();
    } catch (err) {
      console.error(`Failed to ${action} review`, err);
    }
  };



  useEffect(() => {
    fetchReviews();
  }, [status, providerId, serviceProviderId]);

  return { reviews, loading, handleAction };
};

// ====== UI Components ======
const SearchBar: React.FC<{ value: string; onChange: (val: string) => void; onClear: () => void }> = ({ value, onChange, onClear }) => (
  <div className="relative w-full md:w-1/2">
    <input
      type="text"
      placeholder="Search reviews..."
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="px-4 py-2 w-full border border-gray-300 rounded-full shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-300"
    />
    {value && (
      <button onClick={onClear} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700">
        <X size={20} />
      </button>
    )}
  </div>
);

const TabsDropdown: React.FC<{ tabs: typeof TABS; activeTab: string; onChange: (status: string) => void }> = ({ tabs, activeTab, onChange }) => (
  <select value={activeTab} onChange={(e) => onChange(e.target.value)} className="px-3 py-1.5 border rounded-md text-sm w-full md:w-auto">
    {tabs.map((tab) => (
      <option key={tab.status} value={tab.status}>{tab.label}</option>
    ))}
  </select>
);


const ReviewCard: React.FC<{
  review: Review;
  onAction: (id: number, type: "vet" | "service", action: "approve" | "reject" | "hold") => void;
  showActions: boolean;
}> = ({ review, onAction, showActions }) => {
  // Determine which buttons to show
  const showApprove = showActions || review.status === "on hold";
  const showHold = showActions && review.status !== "on hold"; // hide "Hold" if already on hold
  const showReject = showActions || review.status === "on hold";

  return (
    <div className="bg-white/70 border backdrop-blur-md p-6 rounded-3xl shadow-lg hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 group">
      <div className="flex justify-between items-start mb-3">
        <div className="flex items-start gap-2">
          <div>
            <h3 className="text-xl font-semibold text-gray-800">{review.reviewer}</h3>
            {review.reviewer_email && <p className="text-sm text-gray-500">{review.reviewer_email}</p>}
          </div>
        </div>
      </div>

      <div className="flex gap-[2px] mb-4">
        {[...Array(5)].map((_, i) => (
          <Star key={i} size={18} fill={i < review.rating ? "#facc15" : "#e5e7eb"} className="stroke-yellow-500" />
        ))}
      </div>

      <div className="text-gray-700 text-sm mb-4 leading-relaxed max-h-36 overflow-y-auto break-words pr-2">
        {review.comment}
      </div>

      <p className="text-indigo-600 font-medium mb-2">
        {review.type === "vet" ? "Veterinarian Name : " : "Service Name : "} {review.service_name}
      </p>

      {showActions && (
        <div className="flex gap-3 mt-4">
          {showApprove && (
            <button
              onClick={() => onAction(review.id, review.type, "approve")}
              className="flex items-center gap-1 bg-green-500 hover:bg-green-600 text-white px-4 py-1.5 rounded-full text-sm shadow-md transition"
            >
              <CheckCircle size={16} /> Approve
            </button>
          )}

          {showHold && (
            <button
              onClick={() => onAction(review.id, review.type, "hold")}
              className="flex items-center gap-1 bg-yellow-500 hover:bg-yellow-600 text-white px-4 py-1.5 rounded-full text-sm shadow-md transition"
            >
              <Star size={16} /> On Hold
            </button>
          )}

          {showReject && (
            <button
              onClick={() => onAction(review.id, review.type, "reject")}
              className="flex items-center gap-1 bg-red-500 hover:bg-red-600 text-white px-4 py-1.5 rounded-full text-sm shadow-md transition"
            >
              <ThumbsDown size={16} /> Reject
            </button>
          )}
        </div>
      )}
    </div>
  );
};


// ====== Pagination Component ======
const Pagination: React.FC<{ page: number; totalPages: number; onChange: (newPage: number) => void }> = ({ page, totalPages, onChange }) => (
  <div className="flex justify-center items-center gap-2 mt-6">
    <button
      disabled={page === 1}
      onClick={() => onChange(page - 1)}
      className="flex items-center gap-1 px-3 py-1 border rounded disabled:opacity-50"
    >
      <ChevronLeft size={16} /> Prev
    </button>
    <span>Page {page} of {totalPages}</span>
    <button
      disabled={page === totalPages}
      onClick={() => onChange(page + 1)}
      className="flex items-center gap-1 px-3 py-1 border rounded disabled:opacity-50"
    >
      Next <ChevronRight size={16} />
    </button>
  </div>
);

// ====== Main Panel ======
const ReviewModerationPanel: React.FC = () => {
  const [activeTab, setActiveTab] = useState("pending");
  const [searchTerm, setSearchTerm] = useState("");
  const [ratingFilter, setRatingFilter] = useState<number | null>(null);
  const [providerFilter, setProviderFilter] = useState<number | null>(null);
  const [providers, setProviders] = useState<{ id: number, name: string }[]>([]);
  const [serviceProviders, setServiceProviders] = useState<{ id: number; name: string }[]>([]);
  const [serviceProviderFilter, setServiceProviderFilter] = useState<number | null>(null);
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 6; // reviews per page

  const { reviews, loading, handleAction } = useReviews(activeTab, providerFilter ?? undefined, serviceProviderFilter ?? undefined);
  useEffect(() => {
    const fetchProviders = async () => {
      try {
        const response = await apiClient<{ data: { providers: any[] } }>("/v1/resource/providers/all");
        const mappedProviders = response.data?.providers?.map((p: any) => ({
          id: p.id,
          name: `${p.user?.first_name || ""} ${p.user?.last_name || ""}`.trim() || "Unknown Provider",
        })) || [];
        setProviders(mappedProviders);
      } catch (err) {
        console.error("Error fetching providers", err);
      }
    };

    fetchProviders();
  }, []);
  useEffect(() => {
    const fetchServiceProviders = async () => {
      try {
        const response = await apiClient("/v1/resource/service_providers");
        const mapped = (response as { data: { service_providers: any[] } }).data?.service_providers?.map((p: any) => ({
          id: p.id,
          name: `${p.user?.first_name || ""} ${p.user?.last_name || ""}`.trim() || "Unknown Service Provider",
        })) || [];
        setServiceProviders(mapped);
      } catch (err) {
        console.error("Error fetching service providers", err);
      }
    };
    fetchServiceProviders();
  }, []);
  const filteredReviews = useMemo(() =>
    reviews.filter(r => {
      const searchMatch =
        r.reviewer.toLowerCase().includes(searchTerm.toLowerCase()) ||
        r.service_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        r.comment.toLowerCase().includes(searchTerm.toLowerCase());
      const ratingMatch = ratingFilter ? r.rating === ratingFilter : true;
      return searchMatch && ratingMatch;
    }),
    [reviews, searchTerm, ratingFilter]
  );

  const sortedReviews = useMemo(() =>
    [...filteredReviews].sort((a, b) =>
      sortOrder === "desc"
        ? new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        : new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
    ),
    [filteredReviews, sortOrder]
  );

  // Pagination
  const totalPages = Math.ceil(sortedReviews.length / PAGE_SIZE);
  const paginatedReviews = sortedReviews.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, searchTerm, ratingFilter, providerFilter, serviceProviderFilter]);

  if (loading) return <LoadingScreen message="Fetching Reviews..." layout={false} />;

  return (
    <div>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div className="flex-1 min-w-0">
          <SearchBar value={searchTerm} onChange={setSearchTerm} onClear={() => setSearchTerm("")} />
        </div>

        <div className="w-full md:w-auto"><TabsDropdown tabs={TABS} activeTab={activeTab} onChange={setActiveTab} /></div>

        <div className="w-full md:w-auto flex items-center gap-4">
          <select
            className="px-3 py-1.5 border rounded-md text-sm"
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value as "asc" | "desc")}
          >
            <option value="desc">Newest First</option>
            <option value="asc">Oldest First</option>
          </select>

          <select
            className="px-3 py-1.5 border rounded-md text-sm"
            value={ratingFilter ?? ""}
            onChange={(e) => setRatingFilter(e.target.value ? Number(e.target.value) : null)}
          >
            <option value="">All Stars</option>
            {[5, 4, 3, 2, 1].map(star => (
              <option key={star} value={star}>{star} Star{star > 1 && "s"}</option>
            ))}
          </select>
          <select
            className="px-3 py-1.5 border rounded-md text-sm"
            value={providerFilter ?? ""}
            onChange={(e) => setProviderFilter(e.target.value ? Number(e.target.value) : null)}
          >
            <option value="">All Veterinarians</option>

            {providers.map(p => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
          <select
            className="px-3 py-1.5 border rounded-md text-sm"
            value={serviceProviderFilter ?? ""}
            onChange={(e) => setServiceProviderFilter(e.target.value ? Number(e.target.value) : null)}
          >
            <option value="">All Service Providers</option>
            {serviceProviders.map(sp => (
              <option key={sp.id} value={sp.id}>{sp.name}</option>
            ))}
          </select>

        </div>
      </div>



      {paginatedReviews.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-gray-400 animate-fadeIn">
          <MessageCircle className="w-14 h-14 mb-3 text-indigo-400" />
          <p className="italic text-lg">No reviews under <strong>{activeTab}</strong>.</p>
        </div>
      ) : (
        <>
          <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
            {paginatedReviews.map(review => (
              <ReviewCard
                key={review.id}
                review={review}
                onAction={handleAction}
                showActions={activeTab === "pending" || activeTab === "on hold"}
              />
            ))}
          </div>

          {totalPages > 1 && (
            <Pagination page={currentPage} totalPages={totalPages} onChange={setCurrentPage} />
          )}
        </>
      )}
    </div>
  );
};

export default ReviewModerationPanel;
